import React from "react";

import { SubFormNavigationProps } from "../../components/SubForm";
import { useStrings } from "../../localization/localizationHelpers";
import { CurrentGameConcept } from "../../models/crmStatus";
import { Programs } from "../../models/programsConfig";
import { SubFormsRenderingModel } from "../../models/sharedFormsModels";
import {
  AaaGameInformationFormId,
  AaaGameInformationSubFormsId,
  AaaGameInformationSubFormsIdsOrder,
} from "../../subForms/aaaGameInformation/AaaGameInformationPageConstants";
import { AvailabilitySubForm } from "../../subForms/aaaGameInformation/AvailabilitySubForm";
import { ConceptDetailsSubForm as AaaConceptDetailsSubForm } from "../../subForms/aaaGameInformation/ConceptDetailsSubForm";
import { ConfigurationsSubForm as AaaConfigurationsSubForm } from "../../subForms/aaaGameInformation/ConfigurationsSubForm";
import { DistributionInformationSubForm } from "../../subForms/aaaGameInformation/DistributionInformationSubForm";
import { GameExpectationsSubForm as AaaGameExpectationsSubForm } from "../../subForms/aaaGameInformation/GameExpectationsSubForm";
import AaaSupplementaryDataSubForm from "../../subForms/aaaGameInformation/SupplementaryDataSubForm/SupplementaryDataSubForm";
import { TechnicalCapabilitiesSubForm as AaaTechnicalCapabilitiesSubForm } from "../../subForms/aaaGameInformation/TechnicalCapabilitiesSubForm";
import AaaUgcSubForm from "../../subForms/aaaGameInformation/UgcSubForm/UgcSubForm";
import { XboxNetworkSubForm as AaaXboxNetworkSubForm } from "../../subForms/aaaGameInformation/XboxNetworkSubForm";
import IdConceptDetailsSubForm from "../../subForms/idXboxGameInformation/ConceptDetailsSubForm/ConceptDetailsSubForm";
import IdConfigurationsSubForm from "../../subForms/idXboxGameInformation/ConfigurationsSubForm/ConfigurationsSubForm";
import { GameExpectationsSubForm as IdGameExpectationsSubForm } from "../../subForms/idXboxGameInformation/GameExpectationsSubForm";
import {
  GameInformationFormId,
  GameInformationSubFormsId,
  GameInformationSubFormsIdsOrder,
} from "../../subForms/idXboxGameInformation/GameInformationPageConstants";
import IdPricingAndAvailabilitySubForm from "../../subForms/idXboxGameInformation/PricingAndAvailabilitySubForm/PricingAndAvailabilitySubForm";
import IdSupplementaryDataSubForm from "../../subForms/idXboxGameInformation/SupplementaryDataSubForm/SupplementaryDataSubForm";
import IdTechnicalCapabilitiesSubForm from "../../subForms/idXboxGameInformation/TechnicalCapabilitiesSubForm/TechnicalCapabilitiesSubForm";
import { UgcSubForm as IdUgcSubForm } from "../../subForms/idXboxGameInformation/UgcSubForm";
import IdXboxNetworkSubForm from "../../subForms/idXboxGameInformation/XboxNetworkSubForm/XboxNetworkSubForm";

type GameConceptFormConfig = Readonly<{
  subFormsOrder: ReadonlyArray<string>;
  formId: string;
  program: Programs;
  subFormsList: ReadonlyDictionary<SubFormsRenderingModel>;
}>;

export const useGameConceptFormConfig = (
  accountType: "id" | "aaa",
  currentGameConcept?: CurrentGameConcept
): GameConceptFormConfig => {
  const [
    conceptDetailsTitle,
    gameExpectationsTitle,
    pricingAndAvailabilityTitle,
    technicalCapabilitiesTitle,
    configurationsTitle,
    xboxNetworkTitle,
    ugcTitle,
    supplementaryDataTitle,
    distributionInformationTitle,
    availabilityTitle,
  ] = useStrings(
    "gameConceptConceptDetailsTitle",
    "gameConceptGameExpectationsTitle",
    "gameConceptPricingAndAvailabilityTitle",
    "gameConceptTechnicalCapabilitiesTitle",
    "gameInformationConfigurationsTitle",
    "gameConceptXboxNetworkTitle",
    "gameConceptUgcTitle",
    "gameConceptSupplementaryDataTitle",
    "gameConceptDistributionInformationTitle",
    "gameInformationAvailabilityTitle"
  );

  if (accountType === "aaa") {
    return {
      subFormsOrder: AaaGameInformationSubFormsIdsOrder,
      formId: AaaGameInformationFormId,
      program: Programs.Aaa,
      subFormsList: {
        [AaaGameInformationSubFormsId.ConceptDetails]: {
          title: conceptDetailsTitle,
          renderSubForm: (props: SubFormNavigationProps) => <AaaConceptDetailsSubForm {...props} />,
        },
        [AaaGameInformationSubFormsId.DistributionInformation]: {
          title: distributionInformationTitle,
          renderSubForm: (props: SubFormNavigationProps) => (
            <DistributionInformationSubForm {...props} />
          ),
        },
        [AaaGameInformationSubFormsId.GameExpectations]: {
          title: gameExpectationsTitle,
          renderSubForm: (props: SubFormNavigationProps) => (
            <AaaGameExpectationsSubForm {...props} currentGameConcept={currentGameConcept} />
          ),
        },
        [AaaGameInformationSubFormsId.Availability]: {
          title: availabilityTitle,
          renderSubForm: (props: SubFormNavigationProps) => <AvailabilitySubForm {...props} />,
        },
        [AaaGameInformationSubFormsId.TechnicalCapabilities]: {
          title: technicalCapabilitiesTitle,
          renderSubForm: (props: SubFormNavigationProps) => (
            <AaaTechnicalCapabilitiesSubForm {...props} />
          ),
        },
        [AaaGameInformationSubFormsId.Configurations]: {
          title: configurationsTitle,
          renderSubForm: (props: SubFormNavigationProps) => <AaaConfigurationsSubForm {...props} />,
        },
        [AaaGameInformationSubFormsId.XboxNetwork]: {
          title: xboxNetworkTitle,
          renderSubForm: (props: SubFormNavigationProps) => <AaaXboxNetworkSubForm {...props} />,
        },
        [AaaGameInformationSubFormsId.Ugc]: {
          title: ugcTitle,
          renderSubForm: (props: SubFormNavigationProps) => <AaaUgcSubForm {...props} />,
        },
        [AaaGameInformationSubFormsId.SupplementaryData]: {
          title: supplementaryDataTitle,
          renderSubForm: (props: SubFormNavigationProps) => (
            <AaaSupplementaryDataSubForm {...props} />
          ),
        },
      },
    };
  }

  return {
    subFormsOrder: GameInformationSubFormsIdsOrder,
    formId: GameInformationFormId,
    program: Programs.IdXbox,
    subFormsList: {
      [GameInformationSubFormsId.ConceptDetails]: {
        title: conceptDetailsTitle,
        renderSubForm: (props: SubFormNavigationProps) => <IdConceptDetailsSubForm {...props} />,
      },
      [GameInformationSubFormsId.GameExpectations]: {
        title: gameExpectationsTitle,
        renderSubForm: (props: SubFormNavigationProps) => (
          <IdGameExpectationsSubForm {...props} currentGameConcept={currentGameConcept} />
        ),
      },
      [GameInformationSubFormsId.PricingAndAvailability]: {
        title: pricingAndAvailabilityTitle,
        renderSubForm: (props: SubFormNavigationProps) => (
          <IdPricingAndAvailabilitySubForm {...props} />
        ),
      },
      [GameInformationSubFormsId.TechnicalCapabilities]: {
        title: technicalCapabilitiesTitle,
        renderSubForm: (props: SubFormNavigationProps) => (
          <IdTechnicalCapabilitiesSubForm {...props} />
        ),
      },
      [GameInformationSubFormsId.Configurations]: {
        title: configurationsTitle,
        renderSubForm: (props: SubFormNavigationProps) => <IdConfigurationsSubForm {...props} />,
      },
      [GameInformationSubFormsId.XboxNetwork]: {
        title: xboxNetworkTitle,
        renderSubForm: (props: SubFormNavigationProps) => <IdXboxNetworkSubForm {...props} />,
      },
      [GameInformationSubFormsId.Ugc]: {
        title: ugcTitle,
        renderSubForm: (props: SubFormNavigationProps) => <IdUgcSubForm {...props} />,
      },
      [GameInformationSubFormsId.SupplementaryData]: {
        title: supplementaryDataTitle,
        renderSubForm: (props: SubFormNavigationProps) => <IdSupplementaryDataSubForm {...props} />,
      },
    },
  };
};
